import './style.modules.scss'

import {
  Button,
  ButtonVariant,
  Chip,
  ChipGroup,
  ChipVariant
} from '@ampcore/adel-react'
import React, { useState } from 'react'

import { Image } from '../Image/Image'
import { ChipNavigatorProps } from './ChipNavigator.type'

declare global {
  // eslint-disable-next-line no-var
  var _satellite: { track: (event: string, data?: string) => void } | undefined
}

export const ChipNavigator = ({
  heading,
  description,
  options,
  actionUrl,
  paramName = 'category',
  ctaLabel,
  backgroundImage,
  backgroundImageSm,
  testId
}: ChipNavigatorProps): JSX.Element => {
  const [selections, setSelections] = useState<Record<string, boolean>>({})
  const toggle = (value: string) =>
    setSelections(prev => ({ ...prev, [value]: !prev[value] }))

  const selectedValues = Object.entries(selections)
    .filter(([, selected]) => selected)
    .map(([value]) => value)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: This sessionStorage logic is coupled to a specific instance (Resource Center). Remove in future.
    if (selectedValues.length === 0) {
      sessionStorage.removeItem('resourceCenterFilters')
      sessionStorage.removeItem('resourceCenterSubFilters')
    }
    // TODO: This analytics call is coupled to a specific instance (Resource Center). Remove in future.
    if (
      globalThis._satellite &&
      typeof globalThis._satellite.track === 'function'
    ) {
      const topics = selectedValues
        .map(v => options.find(o => o.value === v)?.label ?? v)
        .join(', ')
      globalThis._satellite.track('getSelectedResources', topics)
    }
    const url =
      selectedValues.length > 0
        ? `${actionUrl}?${paramName}=${selectedValues.join(',')}`
        : actionUrl
    globalThis.location.assign(url)
  }

  return (
    <section className="ChipNavigator" data-testid={testId || null}>
      <div className="u-sizeConstrained">
        <div className="ChipNavigator-wrapper">
          {backgroundImage && (
            <div
              className="ChipNavigator-imageWrapper"
              data-testid={testId ? `${testId}-image` : null}
            >
              <Image
                src={backgroundImage}
                sm={backgroundImageSm}
                alt=""
                className="ChipNavigator-image"
              />
            </div>
          )}
          <div className="ChipNavigator-content">
            <div className="Card ChipNavigator-card">
              <h3
                className="u-marginBottom16"
                data-testid={testId ? `${testId}-heading` : null}
              >
                {heading}
              </h3>
              {description && (
                <p data-testid={testId ? `${testId}-description` : null}>
                  {description}
                </p>
              )}
              <ChipGroup
                groupType={ChipVariant.FILTER}
                role="group"
                ariaLabel={heading}
                className="u-marginTop16"
                testId={testId ? `${testId}-chip-group` : undefined}
              >
                {options.map(option => (
                  <Chip
                    key={option.value}
                    variant={ChipVariant.FILTER}
                    label={option.label}
                    selected={!!selections[option.value]}
                    onToggle={() => toggle(option.value)}
                  />
                ))}
              </ChipGroup>
              <hr className="u-marginVert32" />
              <form
                onSubmit={handleSubmit}
                action={actionUrl}
                method="GET"
                data-testid={testId ? `${testId}-form` : null}
              >
                <input
                  type="hidden"
                  name={paramName}
                  value={selectedValues.join(',')}
                />
                <Button
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  className="ChipNavigator-cta"
                  testId={testId ? `${testId}-cta` : undefined}
                >
                  {ctaLabel}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
